import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Box, Save, Loader2, Upload } from 'lucide-react';
import { bundleService } from '../../../services/bundleService'; // Need to expose create/update in main bundleService
import { productService } from '../../../services/productService';
import { sellerProductService } from '../../../services/sellerProductService';
import { componentService } from '../../../services/componentService';
import { imageUploadService } from '../../../utils/imageUploadService';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import BundlePreviewModal from '../../shopping-cart/components/BundlePreviewModal';

const SellerBundleManagement = ({ sellerId }) => {
  const { user } = useAuth();
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBundle, setEditingBundle] = useState(null);
  const [previewBundle, setPreviewBundle] = useState(null);
  const [deletingBundleId, setDeletingBundleId] = useState(null); // NEW: styled confirm modal instead of browser popup
  
  // Form State
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    bundle_price: '',
    discount_percentage: '',
    image_url: '',
    active: true,
    items: []
  });

  const [uploadingImage, setUploadingImage] = useState(false);

  // Item Search State
  const [itemSearchType, setItemSearchType] = useState('product');
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedSearchItems, setSelectedSearchItems] = useState([]);
  const [searchingItems, setSearchingItems] = useState(false);
  useEffect(() => {
    if (user?.id) {
        fetchSellerBundles();
    }
  }, [user]);

  const fetchSellerBundles = async () => {
    try {
      setLoading(true);
      // We need a way to filter bundles by created_by (seller)
      // Assuming 'created_by' column exists on bundles table or we add it
      const { data, error } = await supabase
        .from('bundles')
        .select(`
            *,
            items:bundle_items (count)
        `)
        .eq('seller_id', sellerId); // Filter by seller account ID

      if (error) throw error;
      setBundles(data || []);
    } catch (error) {
      console.error('Error fetching seller bundles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchItems = async () => {
    if (!itemSearchQuery) return;
    try {
      setSearchingItems(true);
      console.log(`Searching ${itemSearchType}s for: "${itemSearchQuery}"`);
      
      let results = [];
      const filter = { 
          searchTerm: itemSearchQuery, 
          itemsPerPage: 10,
          createdBy: user.id // STRICTLY FILTER BY SELLER
      };

      if (itemSearchType === 'product') {
        const res = await sellerProductService.getSellerProductsBySellerId(sellerId);
        results = res.map(sp => ({
            id: sp.productId,
            productId: sp.productId,
            type: 'product', // Explicitly tag type
            name: sp.productName || 'Unknown Product',
            price: sp.price || sp.productGlobalPrice || 0,
            thumbnailUrl: sp.sellerImageUrls?.[0] 
                ? (sp.sellerImageUrls[0].startsWith('http') || sp.sellerImageUrls[0].startsWith('/assets') || sp.sellerImageUrls[0].startsWith('/api') ? sp.sellerImageUrls[0] : imageUploadService.getPublicUrl(sp.sellerImageUrls[0], 'seller-product-images')) 
                : (sp.productThumbnailPath ? (sp.productThumbnailPath.startsWith('http') || sp.productThumbnailPath.startsWith('/assets') || sp.productThumbnailPath.startsWith('/api') ? sp.productThumbnailPath : imageUploadService.getPublicUrl(sp.productThumbnailPath, 'product-thumbnails')) : null)
        }));
        
        if (itemSearchQuery && results.length > 0) {
            results = results.filter(i => i.name.toLowerCase().includes(itemSearchQuery.toLowerCase()));
        }
      } else {
        const res = await componentService.getAll({ ...filter, sellerId: sellerId }); // Pass sellerId context if needed
        results = (res || []).map(c => ({
            ...c,
            type: 'component', // Explicitly tag type
            thumbnailUrl: c.images && c.images.length > 0 
                ? (c.images[0].startsWith('http') || c.images[0].startsWith('/assets') || c.images[0].startsWith('/api') ? c.images[0] : imageUploadService.getPublicUrl(c.images[0])) 
                : null
        }));
        if (itemSearchQuery && results.length > 0 && !res.filteredBySearch) {
             results = results.filter(i => (i.customName || i.name).toLowerCase().includes(itemSearchQuery.toLowerCase()));
        }
      }
      setSearchResults(results.slice(0, 10));
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setSearchingItems(false);
    }
  };

  const toggleSearchItemSelection = (item) => {
    setSelectedSearchItems(prev => {
      if (prev.some(i => i.id === item.id)) {
        return prev.filter(i => i.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  };

  const handleAddSelectedItems = () => {
    if (selectedSearchItems.length === 0) return;
    
    const newItems = selectedSearchItems.map(item => ({
      tempId: Date.now() + Math.random(),
      product_id: item.type === 'product' ? (item.productId || item.id) : null,
      component_id: item.type === 'component' ? (item.globalComponentId || item.componentId) : null,
      quantity: 1,
      name: item.type === 'component' ? `${item.name} (${item.product?.name || 'Unknown Product'})` : item.name,
      price: item.price || 0,
      thumbnail: item.thumbnailUrl || (item.images && item.images[0]) || item.thumbnailPath
    }));

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, ...newItems]
    }));
    
    setSearchResults([]);
    setItemSearchQuery('');
    setSelectedSearchItems([]);
  };

  const handleAddItem = (item) => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          tempId: Date.now() + Math.random(),
          product_id: item.type === 'product' ? (item.productId || item.id) : null,
          component_id: item.type === 'component' ? (item.globalComponentId || item.componentId) : null,
          quantity: 1,
          name: item.type === 'component' ? `${item.name} (${item.product?.name || 'Unknown Product'})` : item.name,
          price: item.price || 0,
          thumbnail: item.thumbnailUrl || (item.images && item.images[0]) || item.thumbnailPath
        }
      ]
    }));
    setSearchResults([]);
    setItemSearchQuery('');
    setSelectedSearchItems([]);
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleUpdateItemQuantity = (index, qty) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index].quantity = parseInt(qty) || 1;
      return { ...prev, items: newItems };
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingImage(true);
      const folder = `bundles/${user.id}/${Date.now()}`;
      const paths = await imageUploadService.uploadImages([file], folder, 'product-thumbnails');
      
      if (paths && paths.length > 0) {
        const publicUrl = imageUploadService.getPublicUrl(paths[0], 'product-thumbnails');
        if (publicUrl) {
          setFormData(prev => ({ ...prev, image_url: publicUrl }));
        }
      }
    } catch (err) {
      console.error("Upload failed", err);
      alert("Failed to upload image: " + err.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let imageUrlToSave = formData.image_url;
      if (imageUrlToSave === '') {
          imageUrlToSave = null;
      } else if (!imageUrlToSave && formData.items.length > 0) {
          const firstImageItem = formData.items.find(i => i.thumbnail);
          if (firstImageItem) {
              imageUrlToSave = firstImageItem.thumbnail;
          }
      }

      const payload = {
        ...formData,
        created_by: null, // Avoid FK issues
        seller_id: sellerId, // Pass sellerId explicitly for store linkage
        image_url: imageUrlToSave,
        bundle_price: formData.bundle_price ? parseFloat(formData.bundle_price) : null,
        discount_percentage: formData.discount_percentage ? parseFloat(formData.discount_percentage) : null,
        items: formData.items.map(item => {
          let productId = null;
          let componentId = null;

          if (item.product_id) productId = item.product_id;
          else if (item.productId) productId = item.productId;
          else if (item.type === 'product') productId = item.id;

          if (item.component_id) componentId = item.component_id;
          else if (item.componentId) componentId = item.componentId;
          else if (item.globalComponentId) componentId = item.globalComponentId;
          else if (item.type === 'component') componentId = item.id;

          productId = productId ? String(productId) : null;
          componentId = componentId ? String(componentId) : null;

          if (!productId && !componentId) {
             console.error("CRITICAL: Both productId and componentId are null for item:", item);
          }

          return {
            product_id: productId,
            component_id: componentId,
            quantity: item.quantity
          };
        })
      };

      if (editingBundle) {
        // Reuse admin service logic but ensure ownership check via RLS or backend
        await bundleService.updateBundle(editingBundle.id, payload);
      } else {
        await bundleService.createBundle(payload);
      }

      setIsModalOpen(false);
      setEditingBundle(null);
      fetchSellerBundles();
    } catch (error) {
      console.error('Error saving bundle:', error);
      alert('Failed to save bundle: ' + error.message);
    }
  };

  const openEditModal = async (bundle) => {
    try {
      // Need a way to fetch full bundle details including items
      // Reusing admin service for now, assuming RLS allows read of own bundles
      const fullBundle = await bundleService.getBundle(bundle.id);
      
      setEditingBundle(fullBundle);
      setFormData({
        name: fullBundle.name,
        description: fullBundle.description || '',
        category: fullBundle.category || '',
        bundle_price: fullBundle.bundle_price || '',
        discount_percentage: fullBundle.discount_percentage || '',
        image_url: fullBundle.image_url
          ? (fullBundle.image_url.startsWith('http') || fullBundle.image_url.startsWith('/assets') || fullBundle.image_url.startsWith('/api')
              ? fullBundle.image_url
              : imageUploadService.getPublicUrl(fullBundle.image_url, 'product-thumbnails'))
          : '',
        active: fullBundle.active,
        items: fullBundle.items.map((item, idx) => ({
          tempId: item.id || `existing-${idx}`,
          product_id: item.product_id,
          component_id: item.component_id,
          quantity: item.quantity,
          name: item.name || 'Unknown Item',
          price: item.price || 0,
          thumbnail: item.thumbnail || null
        }))
      });
      setIsModalOpen(true);
    } catch (err) {
      console.error('Error loading bundle details:', err);
      alert('Failed to load bundle details');
    }
  };

  const handleDelete = (id) => {
    setDeletingBundleId(id);
  };

  const handlePreviewBundle = async (bundle) => {
    try {
      // Fetch full details with items for accurate preview
      const fullBundle = await bundleService.getBundle(bundle.id);
      setPreviewBundle(fullBundle);
    } catch (err) {
      console.error('Error loading bundle for preview:', err);
      alert('Failed to load bundle preview');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">Your Bundles</h3>
        <button
          onClick={() => {
            setEditingBundle(null);
            setFormData({
              name: '', description: '', category: '', bundle_price: '', discount_percentage: '', image_url: '', active: true, items: []
            });
            setSearchResults([]);
            setItemSearchQuery('');
            setIsModalOpen(true);
          }}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
        >
          <Plus size={20} />
          Add Bundle
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading bundles...</div>
      ) : bundles.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg text-gray-500">
            <Box className="w-12 h-12 mx-auto mb-2 text-gray-400" />
            <p>You haven't created any bundles yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bundle</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {bundles.map((bundle) => (
                <tr 
                  key={bundle.id} 
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handlePreviewBundle(bundle)}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0">
                        {bundle.image_url ? (
                          <img className="h-10 w-10 rounded-full object-cover" src={bundle.image_url.startsWith('http') || bundle.image_url.startsWith('/assets') || bundle.image_url.startsWith('/api') ? bundle.image_url : imageUploadService.getPublicUrl(bundle.image_url, 'product-thumbnails')} alt="" />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                            <Box className="h-5 w-5 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{bundle.name}</div>
                        <div className="text-sm text-gray-500">{bundle.category}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-500">
                    {bundle.items?.[0]?.count || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {bundle.bundle_price ? `$${bundle.bundle_price}` : `${bundle.discount_percentage}% Off`}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      type="button"
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        try {
                          const { error } = await supabase
                            .from('bundles')
                            .update({ active: !bundle.active })
                            .eq('id', bundle.id);
                          if (error) throw error;
                          setBundles(prev => prev.map(b => b.id === bundle.id ? { ...b, active: !b.active } : b));
                        } catch (err) {
                          console.error('Error toggling bundle status:', err);
                        }
                      }}
                      className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full transition-all cursor-pointer border ${
                        bundle.active 
                          ? 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200' 
                          : 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200'
                      }`}
                      title="Click to toggle active status"
                    >
                      {bundle.active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEditModal(bundle); }} className="text-indigo-600 hover:text-indigo-900 mr-4">
                      <Edit2 size={18} />
                    </button>
                    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDelete(bundle.id); }} className="text-red-600 hover:text-red-900">
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal - Reusing the same structure as Admin but scoped data */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            {/* ... Same modal content as Admin, mapped to local handlers ... */}
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">{editingBundle ? 'Edit Bundle' : 'Create Bundle'}</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-500">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
               {/* Form Fields - Copied from admin component but stripped down if needed */}
               <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input type="text" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Category</label>
                  <input type="text" value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea rows={3} value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Bundle Price ($)</label>
                  <input type="number" step="0.01" value={formData.bundle_price} onChange={e => setFormData({...formData, bundle_price: e.target.value})} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2" placeholder="Leave empty for auto-calc" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Discount (%)</label>
                  <input type="number" step="0.01" value={formData.discount_percentage} onChange={e => setFormData({...formData, discount_percentage: e.target.value})} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2" placeholder="e.g. 15" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Image</label>
                <div className="mt-1 flex gap-2 items-center">
                  <div className="relative">
                    <input
                        type="file"
                        id="bundle-image-upload-seller"
                        className="hidden"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                    />
                    <label 
                        htmlFor="bundle-image-upload-seller"
                        className={`cursor-pointer px-3 py-2 border border-gray-300 rounded-md flex items-center gap-2 hover:bg-gray-50 ${uploadingImage ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        <span className="text-sm">Upload</span>
                    </label>
                  </div>
                  {formData.image_url && (
                      <img 
                          src={formData.image_url.startsWith('http') || formData.image_url.startsWith('/assets') || formData.image_url.startsWith('/api') ? formData.image_url : imageUploadService.getPublicUrl(formData.image_url, 'product-thumbnails')} 
                          alt="Preview" 
                          className="h-10 w-10 object-cover rounded border" 
                      />
                  )}
                </div>
              </div>

              <div className="flex items-center">
                <input type="checkbox" checked={formData.active} onChange={e => setFormData({...formData, active: e.target.checked})} className="h-4 w-4 text-blue-600 border-gray-300 rounded" />
                <label className="ml-2 block text-sm text-gray-900">Active</label>
              </div>

              {/* Items Section */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Bundle Items (Your Products/Components Only)</h4>
                
                <div className="bg-gray-50 p-3 rounded-lg mb-4">
                  <div className="flex gap-2 mb-2">
                    <select value={itemSearchType} onChange={e => setItemSearchType(e.target.value)} className="border border-gray-300 rounded-md p-1 text-sm">
                      <option value="product">Product</option>
                      <option value="component">Component</option>
                    </select>
                    <div className="relative flex-1">
                      <input 
                        type="text" 
                        placeholder="Search items..." 
                        value={itemSearchQuery} 
                        onChange={e => setItemSearchQuery(e.target.value)} 
                        className="w-full border border-gray-300 rounded-md p-1 pl-8 text-sm"
                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleSearchItems())}
                      />
                      <Search size={14} className="absolute left-2 top-2 text-gray-400" />
                    </div>
                    <button type="button" onClick={handleSearchItems} className="bg-gray-200 hover:bg-gray-300 px-3 py-1 rounded text-sm">
                        {searchingItems ? '...' : 'Search'}
                    </button>
                  </div>

                  {searchResults.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-md max-h-60 flex flex-col">
                      <ul className="overflow-y-auto flex-1">
                        {searchResults.map(result => {
                          const isSelected = selectedSearchItems.some(i => i.id === result.id);
                          return (
                            <li key={result.id} className="p-2 hover:bg-gray-50 flex justify-between items-center text-sm border-b last:border-0 cursor-pointer" onClick={() => toggleSearchItemSelection(result)}>
                              <div className="flex items-center gap-3 overflow-hidden">
                                <input 
                                  type="checkbox" 
                                  checked={isSelected} 
                                  readOnly 
                                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                                />
                                {result.thumbnailUrl ? (
                                  <img src={result.thumbnailUrl} className="w-6 h-6 rounded object-cover border border-gray-200" alt="" />
                                ) : (
                                  <div className="w-6 h-6 rounded bg-gray-100 flex items-center justify-center border border-gray-200">
                                    <Icon name={itemSearchType === 'component' ? "Settings" : "Package"} size={14} className="text-gray-400" />
                                  </div>
                                )}
                                <span className="truncate">
                                  {itemSearchType === 'component' ? `${result.name} (${result.product?.name || 'Unknown Product'})` : result.name} 
                                  &nbsp;(${result.price ? parseFloat(result.price).toFixed(2) : '0.00'})
                                </span>
                              </div>
                              <button type="button" onClick={(e) => { e.stopPropagation(); handleAddItem(result); }} className="text-blue-600 font-medium ml-2 whitespace-nowrap">Add</button>
                            </li>
                          );
                        })}
                      </ul>
                      {selectedSearchItems.length > 0 && (
                        <div className="p-2 border-t bg-gray-50 flex justify-between items-center">
                           <span className="text-sm font-medium text-gray-600">{selectedSearchItems.length} selected</span>
                           <button 
                             type="button" 
                             onClick={handleAddSelectedItems}
                             className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                           >
                             Add Selected
                           </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  {formData.items.map((item, index) => (
                    <div key={item.tempId} className="flex justify-between items-center bg-gray-50 p-2 rounded border border-gray-200">
                      <div className="flex items-center gap-3">
                        {item.thumbnail ? (
                          <img src={item.thumbnail.startsWith('http') || item.thumbnail.startsWith('/assets') || item.thumbnail.startsWith('/api') ? item.thumbnail : imageUploadService.getPublicUrl(item.thumbnail)} className="w-8 h-8 object-cover rounded border border-gray-200" alt="" />
                        ) : (
                          <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center border border-gray-200">
                            <Icon name={item.component_id ? "Settings" : "Package"} size={16} className="text-gray-400" />
                          </div>
                        )}
                        <div>                           <div className="text-sm font-medium">{item.name}</div>
                           <div className="text-xs text-gray-500">${item.price} each</div>
                         </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <input 
                          type="number" 
                          min="1" 
                          value={item.quantity} 
                          onChange={e => handleUpdateItemQuantity(index, e.target.value)} 
                          className="w-16 border border-gray-300 rounded p-1 text-sm text-center"
                        />
                        <button type="button" onClick={() => handleRemoveItem(index)} className="text-red-500 hover:text-red-700">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2">
                  <Save size={18} />
                  Save Bundle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewBundle && (
        <BundlePreviewModal
          bundle={previewBundle}
          onClose={() => setPreviewBundle(null)}
          onAddBundle={() => {}}
          preview={true}
        />
      )}

      {/* Styled Delete Confirmation Modal Notice */}
      {deletingBundleId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card w-full max-w-md rounded-2xl shadow-xl border p-6 text-center animate-in scale-in duration-150">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 text-red-600">
              <Trash2 size={32} />
            </div>
            
            <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Product Bundle?</h3>
            <p className="text-sm text-muted-foreground leading-normal mb-6">
              Are you sure you want to delete this bundle? This will permanently remove the bundle listing from your store and cannot be undone.
            </p>
            
            <div className="flex gap-4">
              <button 
                type="button" 
                onClick={() => setDeletingBundleId(null)}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 font-bold uppercase tracking-wider text-xs h-11"
              >
                Cancel
              </button>
              <button 
                type="button"
                onClick={async () => {
                  const id = deletingBundleId;
                  setDeletingBundleId(null);
                  try {
                    await bundleService.deleteBundle(id);
                    fetchSellerBundles();
                  } catch (err) {
                    console.error('Error deleting bundle:', err);
                    alert('Failed to delete bundle');
                  }
                }}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-black uppercase tracking-wider text-xs h-11 border-none shadow-md shadow-red-600/10 transition-colors"
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellerBundleManagement;
